import React from 'react'
import cx from 'classnames'
import { Message } from 'sb-react-intl'
import { sentry } from 'common/helpers'

import s from './Text.scss'


export const sizes = [
  '56-56',
  '48-56',
  '38-56',
  '38-48',
  '32-40',
  '28-40',
  '22-32',
  '20-24',
  '18-24',
  '16-24',
  '16-20',
  '14-24',
  '14-16',
  '12-24',
  '12-16',
] as const

export const colors = [
  'white',
  'white-warm',
  'white-cold',
  'gray-30',
  'gray-50',
  'gray-70',
  'gray-80',
  'black',
  'brand',
  'success',
  'error',
  'gray', // TODO deprecated, use gray-70
] as const

export type Size = typeof sizes[number]
export type Color = typeof colors[number]

export type TextProps = {
  id?: string
  className?: string
  children?: React.ReactNode,
  message?: ReactIntlMessage | string
  tag?: string
  inherit?: boolean
  inheritWeight?: boolean
  size?: Size
  align?: 'left' | 'center' | 'right'
  semibold?: boolean
  bold?: boolean
  uppercase?: boolean
  capitalize?: boolean
  muted?: boolean
  color?: Color
  html?: boolean
  italic?: boolean
  letterSpacing?: boolean
  ellipsis?: boolean
  noWrap?: boolean
  onClick?: React.MouseEventHandler<HTMLDivElement>
  dataTestId?: string
}

const getWeight = ({ inherit, inheritWeight, semibold, bold }): string => {
  if (bold) return 'bold'
  if (semibold) return 'semibold'
  if (inherit || inheritWeight) return null
  return 'normal'
}

const Text = React.forwardRef<HTMLElement, TextProps>((props, ref) => {
  const {
    children, id, className, tag = 'div', message,
    inherit, inheritWeight, size = '16-24' /* TODO remove default size value */,
    muted, semibold, bold, align, uppercase, capitalize,
    html, color, italic, letterSpacing, ellipsis, noWrap,
    onClick,
    dataTestId,
  } = props

  // TODO remove this after Jan 1 2020 - added on 2019-12-05 by grammka
  // @ts-ignore
  // eslint-disable-next-line
  if (props.values) {
    if (process.env.LOCALTEST || process.env.WEBPACK === 'dev') {
      sentry.captureMessage('"values" prop in <Text />', {
        extra: {
          id,
          className,
          message,
          size,
          color,
          dataTestId,
        },
      })
    }
  }

  if (onClick && tag !== 'button') {
    console.error('You can\'t use "onClick" without passing tag === "button". Create components ADA friendly!')
  }

  if (size && !sizes.includes(size)) {
    console.error(`Wrong size "${size}" passed to Text component`)
  }

  const weight = getWeight({ inherit, inheritWeight, semibold, bold })

  const rootClassName = cx(className,  {
    [s[`size-${size}`]]: !inherit && size,
    [s[weight]]: Boolean(weight),
    [s.uppercase]: uppercase,
    [s.capitalize]: capitalize,
    [s[`align-${align}`]]: align,
    [s.muted]: muted,
    [s[color]]: color,
    [s.italic]: italic,
    [s.letterSpacing]: letterSpacing,
    [s.ellipsis]: ellipsis,
    [s.noWrap]: noWrap,
  })

  if (message) {
    return (
      <Message
        id={id}
        ref={ref}
        className={rootClassName}
        tag={tag}
        text={message}
        html={html}
        onClick={onClick}
        dataTestId={dataTestId}
      />
    )
  }

  return React.createElement(tag, {
    id,
    ref,
    className: rootClassName,
    'data-testid': dataTestId,
    onClick,
  }, children)
})

Text.displayName = 'Text'


export default Text
